'use client';
import { useEffect, useRef } from 'react';
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');
function getFocusable(container) {
    // No visibility filter (e.g. `offsetParent !== null`): jsdom never
    // computes layout so `offsetParent` is always null there, and a real
    // browser wouldn't render a hidden focusable inside an open modal in the
    // first place — the selector itself (excludes `disabled`, `tabindex="-1"`)
    // is sufficient and portable across environments.
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));
}
/**
 * WCAG-compliant modal focus trap (ux.md "Modals: trap focus, move focus in
 * on open, return focus to trigger on close, `inert` the background";
 * CLAUDE_DESIGN.md §9). Moves focus into the container on mount, cycles Tab/
 * Shift+Tab inside it, and restores focus to the element that had it before
 * the modal opened when `active` goes false / the component unmounts.
 */
export function useFocusTrap(active) {
    const containerRef = useRef(null);
    const previouslyFocused = useRef(null);
    useEffect(() => {
        if (!active)
            return;
        const container = containerRef.current;
        previouslyFocused.current = document.activeElement;
        const focusables = container ? getFocusable(container) : [];
        (focusables[0] ?? container)?.focus();
        function onKeyDown(event) {
            if (event.key !== 'Tab' || !container)
                return;
            const nodes = getFocusable(container);
            if (nodes.length === 0) {
                event.preventDefault();
                return;
            }
            const first = nodes[0];
            const last = nodes[nodes.length - 1];
            const activeElement = document.activeElement;
            if (event.shiftKey && activeElement === first) {
                event.preventDefault();
                last.focus();
            }
            else if (!event.shiftKey && activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            previouslyFocused.current?.focus?.();
        };
    }, [active]);
    return containerRef;
}
